import {useEffect, useRef} from "react";
import {useSelector} from "react-redux";
import {appConfig} from "src/config/appConfig";

// виджет подключает не приложение, а скрипт омнидеска, поэтому window.chaport
// появляется уже после старта приложения и его приходится дожидаться
const WAIT_INTERVAL = 1000
const WAIT_ATTEMPTS = 120

const USER_ROLE_LABELS = {
  'merchant-admin': 'Администратор',
  'merchant': 'Сотрудник',
}

const callChaport = (method, ...args) => {
  const chaport = window.chaport

  if (typeof chaport?.[method] === 'function') {
    chaport[method](...args)
    return true
  }

  // пока виджет грузится, вызовы копятся в очереди chaport.q
  if (typeof chaport?.q === 'function') {
    chaport.q(method, ...args)
    return true
  }

  return false
}

const withoutEmpty = (data) => Object.fromEntries(
  Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== '')
)

// ключи произвольных полей должны совпадать с API-ключами в кабинете Chaport:
// после сохранения поля ключ там уже не редактируется
export const buildVisitorData = ({userData, userId, login, merchantId, endpoint}) => {
  const custom = withoutEmpty({
    login,
    role: USER_ROLE_LABELS[userData?.role] ?? userData?.role,
    userId,
    employeeId: userData?.employee_id,
    merchantid: merchantId,
    endpointid: endpoint?.id,
    endpointName: endpoint?.name,
    appVersion: appConfig.environment.REACT_APP_VERSION,
  })

  return {
    ...withoutEmpty({
      name: userData?.name,
      email: userData?.email,
      phone: userData?.phone,
    }),
    custom,
  }
}

export const useChaport = () => {
  const userData = useSelector(store => store.user?.permissions?.user_data)
  const userId = useSelector(store => store.user?.permissions?.user_id)
  const login = useSelector(store => store.user?.getMy?.username)
  const selectedEndpoint = useSelector(store => store.endpoint?.selectedEndpoint)
  const merchantId = useSelector(store => {
    const selectedEndpointId = store.endpoint?.selectedEndpoint?.id
    return selectedEndpointId
      ? store.user?.permissions?.endpoints?.[selectedEndpointId]?.merchant_id
      : null
  })

  const sentVisitorData = useRef(null)

  useEffect(() => {
    if (!userId) {
      if (sentVisitorData.current) {
        sentVisitorData.current = null
        callChaport('stopSession', true)
      }
      return
    }

    const visitorData = buildVisitorData({
      userData,
      userId,
      login,
      merchantId,
      endpoint: selectedEndpoint,
    })
    const serialized = JSON.stringify(visitorData)

    if (serialized === sentVisitorData.current) {
      return
    }

    const send = () => {
      if (!callChaport('setVisitorData', visitorData)) {
        return false
      }

      sentVisitorData.current = serialized
      return true
    }

    if (send()) {
      return
    }

    let attempts = 0
    const waitForWidget = setInterval(() => {
      attempts += 1

      if (send() || attempts >= WAIT_ATTEMPTS) {
        clearInterval(waitForWidget)
      }
    }, WAIT_INTERVAL)

    return () => clearInterval(waitForWidget)
  }, [userData, userId, login, merchantId, selectedEndpoint])

  return null;
};
